// @ts-nocheck

import { configureStore } from '@reduxjs/toolkit';
import { combineReducers } from 'redux';
import logger from 'redux-logger';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  persistStore,
  persistReducer
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import auth from './auth';
import orgUnits from './orgUnits';
import employee from './employee';
import vehicle from './vehicle';
import order from './order';
import shift from './shift';
import trip from './trip';
import wialonTrip from './wialonTrip';
import vehicleModel from './vehicleModel';
import vehicleModels from './vehicleModels';
import fuelAndOil from './fuelAndOil';
import fuelsAndOils from './fuelsAndOils';
import sidebar from './sidebar';
import journals from './journals';
import journal from './journal';
import company from './company';
import contractor from './contractor';
import contractors from './contractors';
import contractVehicle from './contractVehicle';
import maintenanceOperationGroups from './maintenanceOperationGroups';
import maintenanceOperationGroup from './maintenanceOperationGroup';
import maintenanceOperation from './maintenanceOperation';
import maintenance from './maintenance';
import maintenances from './maintenances';
import repairOrder from './repairOrder';
import role from './role';
import roles from './roles';
import monitoring from './monitoring';
import contractVehicleOrder from './contractVehicleOrder';
import mdmNode from './mdmNode';
import fuelMultiplier from './fuelMultiplier';
import jobTitle from './jobTitle';
import jobTitles from './jobTitles';
import osagoCalculation from './osagoCalculation';
import vehiclePlan from './vehiclePlan';
import vehicleTaxPlan from './vehicleTaxPlan';
import detailedDefectiveStatement from './detailedDefectiveStatement';
import persistFilters from './persistFilters';
import { RESET_SESSION } from './session';
import wialonStore from './wialonStore';
import {
  createSafePersistStorage,
  CURRENT_PERSISTENCE_VERSION,
  migratePersistedState,
  sanitizePersistedState,
} from '../lib/persistedState';

// Стейт списков
export type ListState<T> = {
  data: T[];
  page: number;
  pageSize: number;
  totalCount: number;
};

export type Action = {
  type: string;
  payload: any;
};

const combinedReducer = combineReducers({
  auth,
  orgUnits,
  employee,
  vehicle,
  order,
  shift,
  wialonTrip,
  trip,
  vehicleModel,
  vehicleModels,
  fuelAndOil,
  fuelsAndOils,
  journals,
  journal,
  company,
  contractor,
  contractors,
  contractVehicle,
  sidebar,
  maintenanceOperationGroups,
  maintenanceOperationGroup,
  maintenanceOperation,
  maintenance,
  maintenances,
  repairOrder,
  role,
  roles,
  monitoring,
  contractVehicleOrder,
  mdmNode,
  fuelMultiplier,
  jobTitle,
  jobTitles,
  osagoCalculation,
  vehiclePlan,
  vehicleTaxPlan,
  detailedDefectiveStatement,
  persistFilters,
  wialonStore
});

const rootReducer = (
  state: ReturnType<typeof combinedReducer> | undefined,
  action: Action
) => {
  if (action.type === RESET_SESSION) {
    return combinedReducer(undefined, { type: '@@session/RESET_INIT' });
  }

  return combinedReducer(state, action);
};

// Стейт приложения выводится из фактического root reducer, чтобы не дублировать
// типы ещё не мигрированных Flow-ducks.
export type AppState = ReturnType<typeof rootReducer>;

const defaultCfg = {
  key: 'primary',
  storage,
  whitelist: ['persistFilters', 'wialonStore'],
  version: CURRENT_PERSISTENCE_VERSION,
  migrate: migratePersistedState,
};

export const initStore = (persistCfg: Partial<typeof defaultCfg> = {}) => {
  const cfg = { ...defaultCfg, ...persistCfg };
  const persistLocalStorageKey = `persist:${cfg.key}`;
  const safeStorage = createSafePersistStorage(
    cfg.storage,
    persistLocalStorageKey
  );
  const persistedReducer = persistReducer(
    { ...cfg, storage: safeStorage },
    rootReducer
  );
  const store = configureStore({
    reducer: persistedReducer,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [
            FLUSH,
            REHYDRATE,
            PAUSE,
            PERSIST,
            PURGE,
            REGISTER
          ],
          ignoredPaths: [
            'vehiclePlan.selfVehicles',
            'vehiclePlan.plannedVehicles',
            'vehicleTaxPlan.selfVehicles',
            'vehicleTaxPlan.plannedVehicles'
          ]
        }
      }).concat(logger)
  });

  const persistor = persistStore(store);
  const persistedState = window.localStorage.getItem(persistLocalStorageKey);
  const safePersistedState = sanitizePersistedState(persistedState);

  return {
    store,
    persistor,
    persistStore: safePersistedState.snapshot,
  };
};
